import React, { useState, useEffect } from 'react';
import { Upload, Modal, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getCurrentUser } from '../services/authService';
import { API_URL } from '../config';
import api from '../services/api'; // Importiere die API-Service-Datei

// Funktion zur Konvertierung von Base64 (wird oft für Vorschau benötigt)
function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });
}

const ImageUpload = ({ initialImageList = [], onChange, maxImages = 8 }) => {
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');
    const [fileList, setFileList] = useState([]);

    // Initialisiere fileList basierend auf initialImageList
    useEffect(() => {
        const formattedList = initialImageList.map((img, index) => ({
            uid: img.path || img.url || `${img.name}-${index}-${Date.now()}`, // Eindeutige ID, Pfad oder Name+Index+Timestamp
            name: img.name || `Bild ${index + 1}`,
            status: 'done', // Bereits hochgeladene Bilder
            url: img.url, // URL des Bildes vom Server
            // Optional: ThumbUrl, falls vorhanden
        }));
        setFileList(formattedList);
    }, [initialImageList]);

    const handleCancel = () => setPreviewVisible(false);

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            try {
                 file.preview = await getBase64(file.originFileObj);
            } catch (error) {
                console.error("Error generating preview:", error);
                message.error("Vorschau konnte nicht generiert werden.");
                return;
            }
        }
        setPreviewImage(file.url || file.preview);
        setPreviewVisible(true);
        setPreviewTitle(file.name || (file.url ? file.url.substring(file.url.lastIndexOf('/') + 1) : 'Bildvorschau'));
    };

    const handleChange = ({ fileList: newFileList, file }) => {
        const validFileList = newFileList.filter(f => f.status);
        setFileList(validFileList);

        if (file.status === 'done' || file.status === 'removed' || file.status === 'error') {
            const uploadedFiles = validFileList
                .filter(f => f.status === 'done')
                .map(f => {
                    // Versuche, die Daten aus der Server-Antwort zu extrahieren
                    // Annahme: Die Antwort ist { success: true, count: N, data: [...] }
                    // f.response.data ist dann das Array [...]
                    let responseData = null;
                    if (f.response?.data && Array.isArray(f.response.data)) {
                        // Wenn der Upload nur EIN Bild war (typisch für Antd im non-batch mode),
                        // nimm das erste Element aus dem data-Array der Antwort.
                         if (f.response.data.length > 0) {
                             responseData = f.response.data[0];
                         }
                    } else if (f.response?.data) {
                         // Falls data kein Array ist, aber existiert (weniger wahrscheinlich)
                         responseData = f.response.data;
                    }

                    // Fallback auf bestehende Datei-URL (für Bilder, die schon da waren)
                    const existingUrl = f.url;
                    const existingPublicId = f.publicId; // Hole publicId aus dem File-Objekt
                    const existingName = f.name;

                    // Stelle sicher, dass URL vorhanden ist
                    const finalUrl = responseData?.url || existingUrl;
                    const finalPublicId = responseData?.publicId || existingPublicId; // Hole publicId
                    if (!finalUrl) {
                        console.error("FEHLER: Konnte keine URL für Datei finden:", f);
                    }
                    if (!finalPublicId) {
                        console.warn("WARNUNG: Konnte keine publicId für Datei finden (wichtig zum Löschen!):", f);
                    }

                    return {
                        uid: finalPublicId || f.uid, // Verwende publicId als uid, wenn verfügbar
                        name: responseData?.name || existingName,
                        url: finalUrl,
                        publicId: finalPublicId, // Speichere die publicId
                        size: responseData?.size || f.size,
                        type: responseData?.type || f.type,
                        status: 'done' // Wichtig für Antd
                    };
                });

            // Entferne Bilder ohne URL, bevor onChange aufgerufen wird
            const validUploadedFiles = uploadedFiles.filter(f => f.url);
            if (validUploadedFiles.length !== uploadedFiles.length) {
                console.warn("Einige hochgeladene/verbleibende Dateien hatten keine URL und wurden ignoriert.");
            }

            onChange(validUploadedFiles);
        }

        // Fehlermeldung anzeigen, wenn Upload fehlschlägt
        if (file.status === 'error') {
             let errorMessage = `${file.name} Upload fehlgeschlagen.`;
             if (file.error?.status === 401) {
                 errorMessage = "Authentifizierung fehlgeschlagen. Bitte erneut anmelden.";
             } else if (file.response?.message) {
                 errorMessage = file.response.message; // Nachricht vom Server verwenden
             }
             message.error(errorMessage);
            // Optional: Fehlerhafte Datei aus UI entfernen
            // setFileList(validFileList.filter(f => f.uid !== file.uid));
        }
    };

    // Remove-Handler, um Bild von Cloudinary zu löschen
    const handleRemove = async (file) => {
        // Verhindere Standardverhalten
        // Wenn das Bild eine publicId hat (von Cloudinary hochgeladen), lösche es
        if (file.publicId) {
            console.log(`Versuche Bild mit publicId ${file.publicId} zu löschen`);
            message.loading({ content: 'Bild wird gelöscht...', key: 'deleteImage' });
            try {
                // Verwende den neuen Endpunkt mit publicId
                const response = await api.delete(`/uploads/${file.publicId}`);
                if (response.success) {
                    message.success({ content: 'Bild erfolgreich gelöscht.', key: 'deleteImage' });
                    // Rufe onChange manuell auf, um die Liste zu aktualisieren, NACHDEM die Datei aus fileList entfernt wurde
                    const newFileList = fileList.filter(item => item.uid !== file.uid);
                    onChange(newFileList); // Informiere die Elternkomponente
                    return true; // Erlaube Antd, das Element aus der UI zu entfernen
                } else {
                    message.error({ content: `Fehler beim Löschen: ${response.message}`, key: 'deleteImage' });
                    return false; // Verhindere das Entfernen aus der UI bei Fehler
                }
            } catch (error) {
                console.error("Fehler beim Löschen des Bildes von Cloudinary:", error);
                message.error({ content: `Fehler beim Löschen: ${error.message}`, key: 'deleteImage' });
                return false; // Verhindere das Entfernen aus der UI bei Fehler
            }
        } else {
            console.log("Bild ohne publicId wird aus der Liste entfernt (war nicht hochgeladen oder kein Cloudinary-Bild)", file);
            // Wenn keine publicId vorhanden ist (z.B. Upload fehlgeschlagen oder lokales Bild), einfach entfernen
            const newFileList = fileList.filter(item => item.uid !== file.uid);
            onChange(newFileList); // Informiere die Elternkomponente
            return true; // Erlaube Antd, das Element aus der UI zu entfernen
        }
    };

    const uploadButton = (
        <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Bild hinzufügen</div>
        </div>
    );

    // Korrigierte Upload-URL, passend zur Backend-Route POST /api/uploads
    const uploadUrl = `${API_URL}/uploads`;

    // Token für Authentifizierung
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }

    return (
        <>
            <Upload
                action={uploadUrl} // Verwende die korrigierte URL
                headers={headers} // Authorization Header
                listType="picture-card"
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                onRemove={handleRemove} // Füge den Remove-Handler hinzu
                beforeUpload={(file) => {
                  const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/webp';
                  if (!isJpgOrPng) {
                    message.error('Nur JPG, PNG oder WEBP Dateien sind erlaubt!');
                    return Upload.LIST_IGNORE; // Verhindert Upload
                  }
                  const isLt5M = file.size / 1024 / 1024 < 5; // Limit auf 5MB
                  if (!isLt5M) {
                    message.error('Bild muss kleiner als 5MB sein!');
                     return Upload.LIST_IGNORE; // Verhindert Upload
                  }
                  return true; // Lässt Upload zu (wenn Typ und Größe passen)
                }}
                name="images" // Feldname für das Backend
                multiple={true} // Erlaube Mehrfachauswahl
                accept=".jpg,.jpeg,.png,.webp" // Akzeptierte Dateiendungen
                maxCount={maxImages} // <-- Hinzugefügt: Antd kümmert sich um das Limit
            >
                {/* Der Button wird automatisch von Antd ausgeblendet, wenn maxCount erreicht ist */}
                {/* {fileList.length >= maxImages ? null : uploadButton} <-- Kann entfernt werden */}
                {uploadButton} {/* Button wird jetzt von Antd gesteuert */}
            </Upload>
            <Modal open={previewVisible} title={previewTitle} footer={null} onCancel={handleCancel}>
                <img alt="Vorschau" style={{ width: '100%' }} src={previewImage} />
            </Modal>
        </>
    );
};

export default ImageUpload; 